<section class="scheduled-sync-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Scheduled hotspot sync</p>
      <h2>Configuration-backed sync monitoring</h2>
      <p>Monitor background sync runs for hotspot passengers and sessions without mixing them into manual sync controls.</p>
    </div>
    <button class="secondary-action" type="button" [disabled]="isLoading" (click)="loadHistory()">
      <i class="material-icons">refresh</i>
      <span>Refresh</span>
    </button>
  </div>
  <p class="last-loaded" *ngIf="lastLoadedAt">Last refreshed {{ lastLoadedAt | date:'mediumTime' }}. This view auto-refreshes every 30 seconds.</p>

  <section class="config-panel">
    <div>
      <p class="eyebrow">Configuration</p>
      <h3>Controlled by Hotspot:Sync</h3>
      <p>Scheduled sync is disabled by default with <strong>Hotspot:Sync:Enabled=false</strong>. When enabled, the background worker periodically syncs hotspot passengers and sessions, then records results as hotspot sync runs.</p>
    </div>
    <div class="config-list">
      <span><i class="material-icons">schedule</i>Frequency</span>
      <span><i class="material-icons">group</i>Passenger page size</span>
      <span><i class="material-icons">wifi_tethering</i>Session batch size</span>
      <span><i class="material-icons">format_list_numbered</i>Max batches</span>
    </div>
  </section>

  <div class="loading-state" *ngIf="isLoading">
    <i class="material-icons">sync</i>
    <span>Refreshing scheduled sync history...</span>
  </div>

  <div class="history-grid">
    <section class="history-panel">
      <div class="history-header">
        <div>
          <p class="eyebrow">All runs</p>
          <h3>Recent sync activity</h3>
        </div>
        <span class="total-chip">{{ getTotalLabel(allRunTotal, allRuns.length) }}</span>
      </div>
      <div class="inline-error panel-error" *ngIf="allRunsError">{{ allRunsError }}</div>

      <div class="panel-loading" *ngIf="isLoadingAllRuns && !allRuns.length">
        <i class="material-icons">sync</i>
        <span>Loading all sync runs...</span>
      </div>

      <div class="table-scroll" *ngIf="allRuns.length; else noAllRuns">
        <table>
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Resource</th>
              <th>Type</th>
              <th>Status</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Requested</th>
              <th>Processed</th>
              <th>Created mappings</th>
              <th>Errors</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let run of allRuns; trackBy: trackRun">
              <td><strong>{{ getRunId(run) }}</strong></td>
              <td>{{ run.resource || '-' }}</td>
              <td>{{ getSyncType(run) }}</td>
              <td><span class="status-badge">{{ run.status || '-' }}</span></td>
              <td>{{ getRunTime(run, 'started') }}</td>
              <td>{{ getRunTime(run, 'completed') }}</td>
              <td>{{ getRequested(run) }}</td>
              <td>{{ getProcessed(run) }}</td>
              <td>{{ getCreatedMappings(run) }}</td>
              <td>{{ getErrorCount(run) }}</td>
              <td>{{ getRunDetail(run) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noAllRuns>
        <div class="empty-state" *ngIf="!isLoadingAllRuns">
          <i class="material-icons">history</i>
          <h3>No sync runs returned by the API</h3>
          <p>The sync-runs endpoint is reachable, but it returned zero rows for this environment.</p>
        </div>
      </ng-template>
    </section>

    <section class="history-panel">
      <div class="history-header">
        <div>
          <p class="eyebrow">Passengers</p>
          <h3>{{ getResourceLabel('Passengers') }}</h3>
        </div>
        <span class="total-chip">{{ getTotalLabel(passengerRunTotal, passengerRuns.length) }}</span>
      </div>
      <div class="inline-error panel-error" *ngIf="passengerRunsError">{{ passengerRunsError }}</div>

      <div class="panel-loading" *ngIf="isLoadingPassengerRuns && !passengerRuns.length">
        <i class="material-icons">sync</i>
        <span>Loading passenger sync runs...</span>
      </div>

      <div class="table-scroll" *ngIf="passengerRuns.length; else noPassengerRuns">
        <table>
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Requested</th>
              <th>Processed</th>
              <th>Created profiles</th>
              <th>Created mappings</th>
              <th>Errors</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let run of passengerRuns; trackBy: trackRun">
              <td><strong>{{ getRunId(run) }}</strong></td>
              <td>{{ getSyncType(run) }}</td>
              <td><span class="status-badge">{{ run.status || '-' }}</span></td>
              <td>{{ getRunTime(run, 'started') }}</td>
              <td>{{ getRunTime(run, 'completed') }}</td>
              <td>{{ getRequested(run) }}</td>
              <td>{{ getProcessed(run) }}</td>
              <td>{{ getCreatedProfiles(run) }}</td>
              <td>{{ getCreatedMappings(run) }}</td>
              <td>{{ getErrorCount(run) }}</td>
              <td>{{ getRunDetail(run) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noPassengerRuns>
        <div class="empty-state" *ngIf="!isLoadingPassengerRuns">
          <i class="material-icons">history</i>
          <h3>No sync runs returned by the API</h3>
          <p>The sync-runs endpoint is reachable, but it returned zero rows for this environment.</p>
        </div>
      </ng-template>
    </section>

    <section class="history-panel">
      <div class="history-header">
        <div>
          <p class="eyebrow">Sessions</p>
          <h3>{{ getResourceLabel('Sessions') }}</h3>
        </div>
        <span class="total-chip">{{ getTotalLabel(sessionRunTotal, sessionRuns.length) }}</span>
      </div>
      <div class="inline-error panel-error" *ngIf="sessionRunsError">{{ sessionRunsError }}</div>

      <div class="panel-loading" *ngIf="isLoadingSessionRuns && !sessionRuns.length">
        <i class="material-icons">sync</i>
        <span>Loading session sync runs...</span>
      </div>

      <div class="table-scroll" *ngIf="sessionRuns.length; else noSessionRuns">
        <table>
          <thead>
            <tr>
              <th>Run ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Requested</th>
              <th>Processed</th>
              <th>Created profiles</th>
              <th>Created mappings</th>
              <th>Errors</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let run of sessionRuns; trackBy: trackRun">
              <td><strong>{{ getRunId(run) }}</strong></td>
              <td>{{ getSyncType(run) }}</td>
              <td><span class="status-badge">{{ run.status || '-' }}</span></td>
              <td>{{ getRunTime(run, 'started') }}</td>
              <td>{{ getRunTime(run, 'completed') }}</td>
              <td>{{ getRequested(run) }}</td>
              <td>{{ getProcessed(run) }}</td>
              <td>{{ getCreatedProfiles(run) }}</td>
              <td>{{ getCreatedMappings(run) }}</td>
              <td>{{ getErrorCount(run) }}</td>
              <td>{{ getRunDetail(run) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noSessionRuns>
        <div class="empty-state" *ngIf="!isLoadingSessionRuns">
          <i class="material-icons">history</i>
          <h3>No sync runs returned by the API</h3>
          <p>The sync-runs endpoint is reachable, but it returned zero rows for this environment.</p>
        </div>
      </ng-template>
    </section>
  </div>
</section>
